import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BusinessProfile { id: string; name: string }
interface ExpenseCategory { id: string; name: string }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profiles: BusinessProfile[];
  categories: ExpenseCategory[];
  defaultDateFrom: string;
  defaultDateTo: string;
}

export function ExpenseAuditReportDialog({
  open, onOpenChange, profiles, categories, defaultDateFrom, defaultDateTo,
}: Props) {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [profileId, setProfileId] = useState<string>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [generating, setGenerating] = useState(false);

  async function handleGenerate() {
    if (!dateFrom || !dateTo) {
      toast({ title: 'Date range required', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    try {
      const blob = await api.downloadExpenseAuditReport({
        dateFrom,
        dateTo,
        businessProfileId: profileId === 'all' ? undefined : profileId,
        categoryId: categoryId === 'all' ? undefined : categoryId,
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `expense-audit-${dateFrom}_to_${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Failed to generate report',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Expense Audit Report</DialogTitle>
          <DialogDescription>
            Generate a consolidated PDF with every expense in the range plus the attached receipts
            (images inline, PDF statements merged as-is). Ready to hand to an auditor.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="audit-from">From</Label>
              <Input id="audit-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="audit-to">To</Label>
              <Input id="audit-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Business profile</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All profiles</SelectItem>
                {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generating}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={generating}>
              {generating
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileDown className="h-4 w-4 mr-2" />}
              Generate PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
